/* eslint-disable react/no-multi-comp */
/* eslint prefer-arrow-callback: [ "error", { "allowNamedFunctions": true } ] */
import type { ReactElement, ReactNode } from 'react';
import { forwardRef, Fragment, useCallback, useState } from 'react';
import { Dialog, DialogActions, DialogContent, IconButton, Slide, Stack } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import type { TransitionProps } from '@mui/material/transitions';
import { ButtonGroup } from '@noah-labs/fe-shared-ui-components';
import { Menu, X } from 'lucide-react';
import type {
  MarkdownRemarkFrontmatterHeaderMenuHeaderLinks,
  Maybe,
} from '../../../generated/types';
import { useBreakpoints } from '../hooks/useBreakpoints';
import { MobileMenu } from './MobileMenu';
import { NavbarLogo } from './NavbarLogo';

const Transition = forwardRef<unknown, TransitionProps & { children: React.ReactElement }>(
  function Transition(props, ref) {
    return <Slide ref={ref} direction="right" {...props} />;
  },
);

type PpMobileHeader = {
  ButtonsSlot?: ReactNode;
  menu: Maybe<Maybe<MarkdownRemarkFrontmatterHeaderMenuHeaderLinks>[]> | undefined;
  withLanguageSwitch?: boolean;
};

const StyledStack = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1, 1.5, 1, 3),
  width: '100%',
}));

export function MobileHeader({
  ButtonsSlot,
  menu,
  withLanguageSwitch,
}: PpMobileHeader): ReactElement {
  const [open, setOpen] = useState<boolean>(false);
  const theme = useTheme();
  const { isMedium } = useBreakpoints();

  const handleDialogOpen = useCallback(() => {
    setOpen(true);
  }, [setOpen]);
  const handleDialogClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  return (
    <Fragment>
      <StyledStack direction="row">
        <NavbarLogo />
        <IconButton
          aria-haspopup
          aria-label="menu"
          data-qa="mobile-menu"
          edge="end"
          onClick={handleDialogOpen}
        >
          <Menu color={theme.palette.websiteBrand.greyscale.black} size={32} />
        </IconButton>
      </StyledStack>
      {isMedium && (
        <Dialog fullScreen open={open} TransitionComponent={Transition} onClose={handleDialogClose}>
          <StyledStack direction="row">
            <NavbarLogo />
            <IconButton onClick={handleDialogClose}>
              <X color={theme.palette.websiteBrand.primary} size={32} />
            </IconButton>
          </StyledStack>
          <DialogContent sx={{ padding: 3 }}>
            <MobileMenu menu={menu} withLanguageSwitch={withLanguageSwitch} />
          </DialogContent>
          <DialogActions>
            <ButtonGroup spacing={1} sx={{ a: { width: '100%' } }}>
              {ButtonsSlot}
            </ButtonGroup>
          </DialogActions>
        </Dialog>
      )}
    </Fragment>
  );
}
